import { PersonStanding } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { FeedbackTray } from "@/components/Feedback/FeedbackTray";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import type { Character } from "@/types/summary";
import { CollapsibleCard } from "./CollapsibleCard";
import { isInsightPresent } from "@/utils/isInsightPresent";
import { UnavailableInsightCard } from "./UnavailableInsightCard";

function CharacterEntry({ character }: { character: Character }) {
  return (
    <div
      className="px-6 py-4 first:pt-0 last:pb-0"
      data-testid="character-entry"
    >
      <div className="relative pl-6">
        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-full bg-primary-500" />

        <div className="mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h4
              className="text-lg leading-6 font-semibold text-foreground"
              data-testid="character-name"
            >
              {character.name}
            </h4>
            {character.role && (
              <Badge variant="role" data-testid="character-role">
                {character.role}
              </Badge>
            )}
            {character.pronouns && (
              <Badge
                variant="demographic"
                data-testid="character-pronouns-badge"
              >
                {character.pronouns}
              </Badge>
            )}
            {character.age && (
              <Badge variant="demographic" data-testid="character-age-badge">
                {character.age}
              </Badge>
            )}
          </div>

          {character.aliases.length > 0 && (
            <span
              className="text-[0.8125rem] leading-5 italic mt-3 block text-secondary-500"
              data-testid="character-aliases"
            >
              aka &ldquo;{character.aliases.join(", ")}&rdquo;
            </span>
          )}
        </div>

        <p
          className="text-sm leading-5 text-secondary-500"
          data-testid="character-description"
        >
          {character.description}
        </p>
      </div>
    </div>
  );
}

interface CharacterCardProps {
  characters?: Character[];
}

export function CharacterCard({ characters }: CharacterCardProps) {
  const { t } = useTranslation("jobDetails");
  const { jobId } = useParams({ from: "/jobs/$jobId", strict: true });
  const { features } = useFeatureFlags();

  if (!isInsightPresent.characters(characters)) {
    if (!features.characters) return null;
    return <UnavailableInsightCard insight="characters" />;
  }

  return (
    <FeedbackTray
      cardTestId="characters"
      jobId={jobId}
      contentType="characters"
      promptKey="feedback.characters.prompt"
    >
      <CollapsibleCard
        icon={PersonStanding}
        title={t("characters.title")}
        testId="characters"
        showExperimentalBadge
      >
        <div className="divide-y divide-secondary-100 mt-5 -mx-6">
          {characters.map((character, index) => (
            <CharacterEntry
              key={`${character.name}-${index}`}
              character={character}
            />
          ))}
        </div>
      </CollapsibleCard>
    </FeedbackTray>
  );
}
